import React, { useCallback, useMemo, useState } from 'react';
import config from '$config';
import MaiaTranslationContext from './MaiaTranslationContext';
import { MAIA_TRANSLATION_SEGMENT_STORAGE_KEY } from './constants';
import {
  getDefaultTranslationSegment,
  getTranslationSegmentsForApp,
} from './segments';
import { loadSegmentMessages } from './loadSegmentMessages';

function readStoredSegment(isMotrio) {
  const allowed = getTranslationSegmentsForApp(isMotrio);
  const defaultSegment = getDefaultTranslationSegment(isMotrio);
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultSegment;
  }
  try {
    const stored = window.localStorage.getItem(MAIA_TRANSLATION_SEGMENT_STORAGE_KEY);
    if (stored && allowed.includes(stored)) {
      return stored;
    }
  } catch {
    // ignore quota / private mode
  }
  return defaultSegment;
}

export default function MaiaTranslationProvider({ children }) {
  const isMotrio = config.segmentGroup === 'MOTRIO';
  const [segment, setSegmentState] = useState(() => readStoredSegment(isMotrio));

  const setSegment = useCallback(
    (next) => {
      const allowed = getTranslationSegmentsForApp(isMotrio);
      const safe = allowed.includes(next) ? next : getDefaultTranslationSegment(isMotrio);
      setSegmentState(safe);
      try {
        window.localStorage.setItem(MAIA_TRANSLATION_SEGMENT_STORAGE_KEY, safe);
      } catch {
        // ignore
      }
    },
    [isMotrio]
  );

  const messages = useMemo(() => {
    const primary = loadSegmentMessages(segment);
    const defaults = loadSegmentMessages(getDefaultTranslationSegment(isMotrio));
    return { ...defaults, ...primary };
  }, [segment, isMotrio]);

  const value = useMemo(
    () => ({
      segment,
      setSegment,
      messages,
    }),
    [segment, setSegment, messages]
  );

  return <MaiaTranslationContext.Provider value={value}>{children}</MaiaTranslationContext.Provider>;
}
